'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Button from '@/components/common/Button';

interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
  /**
   * エラーコンテキスト
   * - global: グローバルエラー（min-h-screen、大きいアイコン）
   * - dashboard: ダッシュボード内エラー（flex-1、小さいアイコン）
   */
  context?: 'global' | 'dashboard';
  /**
   * 戻り先URL
   * @default '/'
   */
  dashboardUrl?: string;
  /**
   * エラーコンテキストの説明（ログ用）
   * @example 'ページ', '管理画面', 'グローバル'
   */
  errorContext?: string;
  /**
   * 戻るボタンのテキスト
   * @example 'トップページに戻る', 'ダッシュボードに戻る', 'マイページに戻る'
   */
  backButtonText?: string;
}

export default function ErrorBoundary({
  error,
  reset,
  context = 'dashboard',
  dashboardUrl = '/',
  errorContext = 'ページ',
  backButtonText,
}: ErrorBoundaryProps) {
  useEffect(() => {
    console.error(`${errorContext} error:`, error);
  }, [error, errorContext]);

  const isGlobal = context === 'global';
  const containerClass = isGlobal
    ? 'min-h-screen flex items-center justify-center bg-mono-5 px-4'
    : 'flex-1 flex items-center justify-center p-4 md:p-8';

  const iconSize = isGlobal ? 'w-20 h-20' : 'w-16 h-16';
  const svgSize = isGlobal ? 'w-10 h-10' : 'w-8 h-8';
  const titleSize = isGlobal ? 'text-2xl' : 'text-xl';

  const getGlobalMessage = () => (
    <>
      申し訳ございません。予期しないエラーが発生しました。
      <br />
      しばらく時間をおいてから再度お試しください。
    </>
  );

  const getDashboardMessage = () => (
    <>
      {errorContext}の読み込み中にエラーが発生しました。
      <br />
      再試行するか、{getMessageBackText()}に戻ってください。
    </>
  );

  const getBackButtonText = () => {
    if (backButtonText) return backButtonText;
    if (dashboardUrl === '/') return 'トップページに戻る';
    if (dashboardUrl.includes('/admin')) return 'ダッシュボードに戻る';
    if (dashboardUrl.includes('/artist')) return 'ダッシュボードに戻る';
    return 'マイページに戻る';
  };

  const getMessageBackText = () => {
    if (backButtonText) {
      return backButtonText.replace('に戻る', '');
    }
    if (dashboardUrl === '/') return 'トップページ';
    if (dashboardUrl.includes('/admin')) return 'ダッシュボード';
    if (dashboardUrl.includes('/artist')) return 'ダッシュボード';
    return 'マイページ';
  };

  return (
    <div className={containerClass}>
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <div
            className={`${iconSize} mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center`}
          >
            <svg
              className={`${svgSize} text-red-500`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className={`${titleSize} font-bold text-mono-100 mb-2`}>
            エラーが発生しました
          </h1>
          <p
            className={`text-mono-70 ${isGlobal ? '' : 'text-sm'} mb-6`}
          >
            {isGlobal ? getGlobalMessage() : getDashboardMessage()}
          </p>
        </div>

        <div className="flex flex-col gap-3 items-center" role="group" aria-label="エラー復旧オプション">
          <Button buttonType="primary" size="sm" onClick={reset} aria-label="ページを再読み込みして再試行する">
            再試行する
          </Button>
          {isGlobal ? (
            <Button
              buttonType="secondary"
              size="sm"
              onClick={() => (window.location.href = dashboardUrl)}
              aria-label={`${getBackButtonText()}へ移動する`}
            >
              {getBackButtonText()}
            </Button>
          ) : (
            <Link href={dashboardUrl} aria-label={`${getBackButtonText()}へ移動する`}>
              <Button buttonType="secondary" size="sm">
                {getBackButtonText()}
              </Button>
            </Link>
          )}
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-4 bg-mono-10 rounded-lg text-left">
            <p className="text-xs text-mono-70 font-mono break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-mono-50 mt-2">
                Digest: {error.digest}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
